import React from 'react';
import { Download, ChevronDown, MapPin, Check, Users, Info } from 'lucide-react';
import './Hero.css';

const Hero: React.FC = () => {
  return (
    <section className="hero" id="top">
      <div className="hero__container">
        <div className="hero__content">
          <div className="hero__badge">Pickleball community app</div>
          <h1>
            Find courts.<br />
            See friends.<br />
            <span className="text-gradient">Plan your next game.</span>
          </h1>
          <p className="hero__subtitle">
            RalliPB helps pickleball players discover courts, check in when they play,
            follow friend activity, create sessions, and coordinate arrivals from one mobile app.
          </p>
          <div className="hero__availability" role="status">
            <Info size={18} />
            <span>RalliPB is not currently available on the Apple App Store or Google Play. Join the beta waitlist for access updates.</span>
          </div>
          <div className="hero__cta">
            <a href="/beta" className="btn btn--primary btn--lg">
              <Download size={18} />
              Join Beta Waitlist
            </a>
            <a href="#features" className="btn btn--outline btn--lg">
              Explore Features
            </a>
          </div>
          <div className="hero__stats">
            <div className="hero__stat">
              <span className="hero__stat-number">5</span>
              <span className="hero__stat-label">Core app tabs</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-number">24h</span>
              <span className="hero__stat-label">Friend activity feed</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-number">2</span>
              <span className="hero__stat-label">Mobile platforms</span>
            </div>
          </div>
        </div>

        <div className="hero__visual">
          <PhoneMockup />
          <div className="hero__float hero__float--1">
            <div className="hero__float-icon hero__float-icon--green">
              <Check size={16} />
            </div>
            <div>
              <div className="hero__float-title">Sarah checked in</div>
              <div className="hero__float-sub">Playing until 3:30 PM</div>
            </div>
          </div>
          <div className="hero__float hero__float--2">
            <div className="hero__float-icon hero__float-icon--blue">
              <Users size={16} />
            </div>
            <div>
              <div className="hero__float-title">Mike is joining</div>
              <div className="hero__float-sub">Arriving in 30 min</div>
            </div>
          </div>
        </div>
      </div>

      <div className="hero__scroll">
        <ChevronDown size={24} />
      </div>
    </section>
  );
};

const PhoneMockup: React.FC = () => (
  <div className="phone">
    <div className="phone__frame">
      <div className="phone__notch" />
      <div className="phone__screen">
        <div className="phone__status">
          <span>9:41</span>
          <div className="phone__status-icons">
            <div className="phone__signal" />
            <div className="phone__wifi" />
            <div className="phone__battery" />
          </div>
        </div>

        <div className="phone__app-header">
          <h3>Courts</h3>
        </div>

        <div className="phone__search-wrap">
          <div className="phone__search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <span>Search courts by name, city, zip...</span>
          </div>
        </div>

        <div className="phone__filters">
          <span className="phone__pill phone__pill--active">All</span>
          <span className="phone__pill">Indoor</span>
          <span className="phone__pill">Outdoor</span>
          <span className="phone__pill">Friends</span>
        </div>

        <div className="phone__cards">
          <div className="phone__card">
            <div className="phone__card-img">
              <MapPin size={28} color="#D1D5DB" />
            </div>
            <div className="phone__card-body">
              <div className="phone__card-row">
                <div className="phone__card-name">Coronado Marriott Resort</div>
                <span className="phone__card-type">Outdoor</span>
              </div>
              <div className="phone__card-address">
                <MapPin size={11} color="#9CA3AF" />
                <span>2000 2nd St, Coronado, CA</span>
              </div>
              <div className="phone__card-stats">
                <span>10 Courts</span>
                <span>-</span>
                <span>Hard</span>
                <span>-</span>
                <span>Lighted</span>
              </div>
              <div className="phone__card-footer">
                <span className="phone__card-hint">Tap to see court info</span>
                <button className="phone__checkin-btn">
                  <MapPin size={12} />
                  Check In
                </button>
              </div>
            </div>
          </div>

          <div className="phone__card">
            <div className="phone__card-img">
              <MapPin size={28} color="#D1D5DB" />
            </div>
            <div className="phone__card-body">
              <div className="phone__card-row">
                <div className="phone__card-name">Action Jackson Pickleball</div>
                <span className="phone__card-type">Outdoor</span>
              </div>
              <div className="phone__card-address">
                <MapPin size={11} color="#9CA3AF" />
                <span>2860 Michelle Drive, Irvine, CA</span>
              </div>
              <div className="phone__card-footer">
                <span className="phone__card-hint">Tap to see court info</span>
                <button className="phone__checkin-btn phone__checkin-btn--active">
                  <Check size={12} />
                  Checked In
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="phone__tabbar">
          <div className="phone__tab">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            <span>Home</span>
          </div>
          <div className="phone__tab">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <span>Schedule</span>
          </div>
          <div className="phone__tab phone__tab--active">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            <span>Courts</span>
          </div>
          <div className="phone__tab">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <span>Social</span>
          </div>
          <div className="phone__tab">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            <span>Profile</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default Hero;
